import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const AboutSection = styled.section`
  background: var(--cream);
  padding: 100px 24px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const AboutLayout = styled.div`
  max-width: 1050px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 50px;

  @media (max-width: 900px) {
    flex-direction: column-reverse;
    gap: 40px;
    align-items: center;
  }
`;

const AboutText = styled.div`
  flex: 1;
`;

const AboutImage = styled.img`
  flex: 0 0 340px;
  max-width: 340px;
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  object-position: center top;
  aspect-ratio: 3 / 4;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(26, 46, 26, 0.08);

  @media (max-width: 900px) {
    flex: none;
    max-width: 100%;
    max-height: 400px;
    object-position: center center;
  }
`;

const SectionLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--sage-light);
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 32px;
  line-height: 1.3;
`;

const WhiteHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: white;
  margin-bottom: 32px;
  line-height: 1.3;
`;

const Pronouns = styled.span`
  font-size: 0.7em;
  font-weight: 400;
  color: var(--text-muted);
  margin-left: 8px;
`;

const BodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const LightBodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const HowIWorkSection = styled.section`
  background: var(--spruce-dark);
  padding: 100px 24px;
`;

const CredentialsSection = styled.section`
  background: var(--frost);
  padding: 100px 24px;
`;

const CredentialsContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const CredentialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 48px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const CredentialCard = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  border-left: 3px solid var(--sage);
  box-shadow: 0 1px 4px var(--shadow);
`;

const CredentialTitle = styled.h3`
  font-family: 'Lora', serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--spruce);
  margin-bottom: 8px;
`;

const CredentialSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--text-muted);
  line-height: 1.6;
`;

const LicenseNote = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-muted);
  text-align: center;
  margin-top: 40px;
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const About = () => {
  useScrollReveal();

  return (
    <>
      <PageHero
        label="About"
        title="About Audrey"
        subtitle="Getting to know your therapist is an important part of the process."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      <AboutSection>
        <AboutLayout>
          <AboutText>
            <SectionHeading className="fade-in">
              Hi, I'm Audrey
            </SectionHeading>
            <BodyText className="fade-in delay-1">
              I am a clinical social worker living and working in Northern Vermont.
              I use a collaborative and conversational approach, with the hope that
              I can support you to build richness and connection in your life.
            </BodyText>
            <BodyText className="fade-in delay-2">
              I primarily practice through a trauma-informed psychodynamic and
              somatic modalities. I believe that therapy, at its best, can help us
              form richer relationships with ourselves, which in turn can help us
              form richer relationships with the world around us.
            </BodyText>
            <BodyText className="fade-in delay-3">
              I am trained in EMDR therapy and completed a two year post-graduate
              fellowship from the Psychodynamic Couples and Family Institute of
              New England. I am licensed to practice therapy in Vermont,
              Massachusetts, California, and Pennsylvania.
            </BodyText>
          </AboutText>
          <AboutImage
            src="/images/audrey.jpg"
            alt="Audrey Kiely, therapist"
            className="fade-in delay-2"
          />
        </AboutLayout>
      </AboutSection>

      <WaveDivider topColor="#eef0eb" bottomColor="#1a2e1a" />

      <HowIWorkSection>
        <Container>
          <SectionLabel className="fade-in">My Approach</SectionLabel>
          <WhiteHeading className="fade-in">How I Work</WhiteHeading>
          <LightBodyText className="fade-in delay-1">
            I offer trauma-informed, neuro-affirming therapy. I practice from an
            anti-oppressive lens, working to highlight your agency and control in
            the therapeutic space.
          </LightBodyText>
          <LightBodyText className="fade-in delay-2">
            My therapeutic style is compassionate and curious, especially when
            encountering internal contradictions. My firmly held affection for
            ambivalence welcomes all of the contradictory parts of you into the
            therapeutic space.
          </LightBodyText>
          <LightBodyText className="fade-in delay-3">
            My work often blends together psychodynamic modalities &mdash; a
            style of therapy that is talk based and insight-oriented &mdash; with
            a somatic approach that highlights presence with whatever you are
            currently experiencing. I find that this approach works particularly
            well for people who have good insight about their experience but
            still feel stuck repeating the same patterns.
          </LightBodyText>
          <LightBodyText className="fade-in delay-4">
            My work highlights the importance of relationship: with ourselves,
            with important people in our life both past and present,
            with the world around us, and within the therapeutic space. I work
            from an understanding that healing comes when we have an opportunity
            to have a different experience, and I believe the therapeutic
            relationship can support a multitude of different experiences. To
            that end, I work to show up authentically in the space. I join you
            in this work as a human, open and aware, to help you feel less
            alone.
          </LightBodyText>
        </Container>
      </HowIWorkSection>

      <WaveDivider topColor="#1a2e1a" bottomColor="#e4e8e1" />

      <CredentialsSection>
        <CredentialsContainer>
          <SectionLabel className="fade-in">
            Training &amp; Credentials
          </SectionLabel>
          <SectionHeading className="fade-in">Background</SectionHeading>
          <CredentialsGrid>
            <CredentialCard className="fade-in delay-1">
              <CredentialTitle>MSW</CredentialTitle>
              <CredentialSubtitle>Master of Social Work</CredentialSubtitle>
            </CredentialCard>
            <CredentialCard className="fade-in delay-2">
              <CredentialTitle>LICSW</CredentialTitle>
              <CredentialSubtitle>
                Licensed Independent Clinical Social Worker
              </CredentialSubtitle>
            </CredentialCard>
            <CredentialCard className="fade-in delay-3">
              <CredentialTitle>EMDR Trained</CredentialTitle>
              <CredentialSubtitle>
                Eye Movement Desensitization and Reprocessing
              </CredentialSubtitle>
            </CredentialCard>
            <CredentialCard className="fade-in delay-4">
              <CredentialTitle>Couples &amp; Family</CredentialTitle>
              <CredentialSubtitle>
                Post-graduate fellowship from the Psychodynamic Couples and
                Family Institute of New England
              </CredentialSubtitle>
            </CredentialCard>
          </CredentialsGrid>
          <LicenseNote className="fade-in">
            Licensed to practice in Vermont, Massachusetts, California, and Pennsylvania
          </LicenseNote>
        </CredentialsContainer>
      </CredentialsSection>

      <WaveDivider topColor="#e4e8e1" bottomColor="#d8ddd5" />

      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Interested in Working Together?</CTAHeading>
          <CTASubtitle>
            Book a free 15-minute phone consultation to see if we're a good fit.
          </CTASubtitle>
          <CTAButton to="/contact">Get in Touch</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default About;
